import { Component, OnInit, ViewChild, ElementRef, Input, OnDestroy, AfterViewInit } from '@angular/core';
import { StatoSessioneService } from '../../../../shared/services/stato-sessione.service';
// @ts-ignore
import Quill from 'quill';
import { Subscription } from 'rxjs';
import { SupportFile } from '../story.component';

@Component({
  selector: 'app-story-viewer',
  templateUrl: './story-viewer.component.html',
  styleUrls: ['./story-viewer.component.scss']
})
export class StoryViewerComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('editor', { static: false }) editorElement: ElementRef;
  @Input() fileName: string;

  refinedStoryString: string = '';
  isCore = true;
  isImage = false;
  isCollapsed = true;
  quill: Quill;
  viewMode: 'edit' | 'analysis' = 'analysis';

  /** True when this is the core story and has no content yet */
  get isEmpty(): boolean {
    return this.isCore && (!this.refinedStoryString || this.refinedStoryString.trim().length === 0);
  }

  private wasInitialized = false;
  private savedContent: string = '';
  private subscriptions: Subscription[] = [];

  constructor(
    private sessioneService: StatoSessioneService,
  ) { }

  ngOnInit(): void {
    this.isCore = !this.fileName;

    if (this.isCore) {
      // Start expanded in edit mode for empty stories
      this.isCollapsed = false;
      this.viewMode = 'edit';

      // CORE STORY LOGIC
      const storySub = this.sessioneService.refinedStoryString$.subscribe(refinedStoryString => {
        this.refinedStoryString = refinedStoryString || '';

        // --- FOCUS FIX FOR CORE STORY ---
        // Only update the editor if it's initialized and its content
        // is genuinely different from the incoming data. This prevents the
        // focus-stealing loop when the user is typing.
        if (this.quill && this.viewMode === 'edit' && this.quill.getText().trim() !== this.refinedStoryString.trim()) {
          this.quill.setText(this.refinedStoryString);
        }
      });
      this.subscriptions.push(storySub);

      const fileNameSub = this.sessioneService.fileName$.subscribe(filename => {
        this.fileName = filename;
      });
      this.subscriptions.push(fileNameSub);

    } else {
      // SUPPORT FILE LOGIC
      const supportFile = this.getSupportFile(this.fileName);
      if (supportFile) {
        this.isImage = supportFile.fileType === 'image';
      }

      const contentSub = this.sessioneService.supportFileContents$.subscribe(contentsMap => {
        const content = contentsMap.get(this.fileName);

        if (content !== undefined) {
          // Always update the local string for things like character count display
          this.refinedStoryString = content;

          // --- THE CRITICAL FOCUS FIX FOR SUPPORT FILES ---
          // Only call quill.setText() if the editor is initialized AND its
          // current text is actually different from the new content from the service.
          // This breaks the self-update loop.
          if (this.quill && this.quill.getText().trim() !== content.trim()) {
            this.quill.setText(content);
          }
        }
      });
      this.subscriptions.push(contentSub);
    }
  }

  // No changes needed in AfterViewInit, OnDestroy, or setViewMode
  ngAfterViewInit() {
    if ((this.isCore && this.viewMode === 'edit') || !this.isCore) {
      if (this.editorElement) {
        setTimeout(() => this.initializeEditor(), 0);
      }
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    if (this.quill) {
      this.quill.off('text-change');
    }
  }

  setViewMode(mode: 'edit' | 'analysis'): void {
    if (this.viewMode === mode) return;

    if (this.viewMode === 'edit' && mode === 'analysis') {
      if (this.quill) {
        this.savedContent = this.quill.getText() || '';
        if (this.isCore) {
          this.sessioneService.updateRefinedStoryString(this.savedContent);
        } else {
          this.sessioneService.updateSupportFileContent(this.fileName, this.savedContent);
        }
      }
    }

    this.viewMode = mode;

    if (mode === 'edit') {
      setTimeout(() => this.initializeEditor(), 0);
    }
  }


  // No changes needed in initializeEditor
  initializeEditor(): void {
    if (!this.editorElement) {
      console.error('Editor element not found');
      return;
    }

    if (this.quill) {
      this.quill.off('text-change');
      const editorElement = this.editorElement.nativeElement;
      while (editorElement.firstChild) {
        editorElement.removeChild(editorElement.firstChild);
      }
    }

    const toolbarOptions = [
      ['bold', 'italic', 'underline', 'strike'],
      ['blockquote', 'code-block'],
      [{ 'header': 1 }, { 'header': 2 }],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      [{ 'size': ['small', false, 'large', 'huge'] }],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'align': [] }],
      ['clean']
    ];

    this.quill = new Quill(this.editorElement.nativeElement, {
      theme: 'snow',
      modules: {
        toolbar: toolbarOptions
      },
      readOnly: false
    });

    if (this.savedContent && this.wasInitialized) {
      this.quill.setText(this.savedContent);
    } else {
      this.quill.setText(this.refinedStoryString || '');
    }

    this.wasInitialized = true;

    this.quill.on('text-change', (delta, oldDelta, source) => {
      if (source === 'user') {
        this.onStoryChange();
      }
    });
  }

  // --- RECOMMENDED CHANGE IN onStoryChange ---
  onStoryChange() {
    if (!this.quill) return;

    const newContent = this.quill.getText();

    // We remove the `this.refinedStoryString = newStory;` line here.
    // The subscription in ngOnInit is the single source of truth for updating
    // the component's state, which prevents race conditions.
    if (this.isCore) {
      this.sessioneService.updateRefinedStoryString(newContent);
    } else {
      this.sessioneService.updateSupportFileContent(this.fileName, newContent);
    }
  }

  // No changes needed in the rest of the methods
  getEstimatedTokens(): number {
    if (!this.refinedStoryString) { return 0; }
    return Math.round(this.refinedStoryString.length / 3.5);
  }

  downloadHtmlFile() {
    const blob = new Blob([this.refinedStoryString], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${this.fileName || 'story'}.txt`;
    a.click();
    window.URL.revokeObjectURL(url);
  }

  getEditorContent(): string {
    return this.refinedStoryString;
  }

  private getSupportFile(fileName: string): SupportFile | undefined {
    const supportFiles = this.sessioneService.getSupportFiles();
    return supportFiles.find(file => file.name === fileName);
  }
}
